import {
    FormControlLabel,
    RadioGroup,
    Radio,
    RadioGroupProps,
    FormHelperText,
    RadioProps,
    styled,
    Typography,
    Box,
    alpha,
} from '@mui/material';
import { colors } from 'common/constants';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import VeryDissatisfied from 'assets/images/icons/question-type/question-emoji/VeryDissatisfiedText.png';
import Dissatisfied from 'assets/images/icons/question-type/question-emoji/DissatisfiedText.png';
import Neutral from 'assets/images/icons/question-type/question-emoji/NeutralText.png';
import Satisfied from 'assets/images/icons/question-type/question-emoji/SatisfiedText.png';
import VerySatisfied from 'assets/images/icons/question-type/question-emoji/VerySatisfiedText.png';
import { common } from '@mui/material/colors';
import { useAppSelector } from 'common/hook';

type IProps = RadioGroupProps & {
    name: string;
    numberOfEmojis: number;
    options: {
        value: number;
        label: string;
    }[];
};
const emojisForFiveStars = [
    VeryDissatisfied,
    Dissatisfied,
    Neutral,
    Satisfied,
    VerySatisfied,
];
const emojisForThreeItems = [VeryDissatisfied, Neutral, VerySatisfied];
const QuestionEmoji: React.FC<IProps> = ({
    name,
    options,
    numberOfEmojis,
    ...props
}) => {
    const newOptions = React.useMemo(() => {
        return Array.from({ length: numberOfEmojis }, (_, index) => ({
            value: index,
            label: `Option ${index + 1}`,
        }));
    }, [numberOfEmojis]);
    const { control } = useFormContext();
    const currentCampaign = useAppSelector(
        (state) => state.campaignState.campaign
    );
    const isCampaignPublicWithCode =
        currentCampaign?.kind === 'public_with_code';
    return (
        <>
            {options.map((item, index) => (
                <Box
                    key={index}
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: 'column',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: [
                                '1.2rem',
                                '1.2rem',
                                '1.2rem',
                                '1.4rem',
                                '1.4rem',
                            ],
                            color: [common.white, common.black],
                            fontWeight: 700,
                            display: isCampaignPublicWithCode
                                ? 'none'
                                : 'block',
                        }}
                    >
                        {item.label}
                    </Typography>
                    <Controller
                        control={control}
                        name={`${name}.${index}.emoji`}
                        render={({ field, fieldState }) => (
                            <>
                                <RadioGroup {...field} {...props}>
                                    {newOptions.map((option) => (
                                        <FormControlLabel
                                            sx={{
                                                margin: 0,
                                                gap: '5px',
                                            }}
                                            key={option.value}
                                            value={option.value}
                                            control={
                                                <BpRadio
                                                    numberOfEmojis={
                                                        numberOfEmojis
                                                    }
                                                />
                                            }
                                            label={''}
                                        />
                                    ))}
                                </RadioGroup>
                                <FormHelperText
                                    sx={{
                                        fontSize: '14px',
                                        marginLeft: 0,
                                        color: `${colors.textError} !important`,
                                        fontWeight: '400',
                                    }}
                                >
                                    {fieldState.error?.message}
                                </FormHelperText>
                            </>
                        )}
                    />
                </Box>
            ))}
        </>
    );
};
function BpRadio(props: RadioProps & { numberOfEmojis: number }) {
    return (
        <Radio
            disableRipple
            color="default"
            checkedIcon={
                <BpCheckedIcon
                    label={props.value as unknown as number}
                    numberOfEmojis={props.numberOfEmojis}
                />
            }
            icon={
                <BpIcon
                    label={props.value as unknown as number}
                    numberOfEmojis={props.numberOfEmojis}
                />
            }
            sx={{ padding: 0 }}
            {...props}
        />
    );
}
const BpIcon = styled('span')<{ label: number; numberOfEmojis: number }>(
    ({ label, numberOfEmojis, theme }) => ({
        width: '3rem',
        height: '3rem',
        backgroundImage: `url(${numberOfEmojis === 5 ? emojisForFiveStars[label] : emojisForThreeItems[label]})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        borderRadius: '10px',
        [theme.breakpoints.up('sm')]: {
            width: '3.5rem',
            height: '3.5rem',
        },
        [theme.breakpoints.up('md')]: {
            width: '5rem',
            height: '5rem',
        },
        [theme.breakpoints.up('xl')]: {
            width: '6rem',
            height: '6rem',
        },
    })
);

const BpCheckedIcon = styled('span')<{ label: number; numberOfEmojis: number }>(
    ({ label, numberOfEmojis, theme }) => ({
        padding: '10px',
        width: '3.5rem',
        height: '3.5rem',
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: common.white,
        opacity: 0.9,
        alignItems: 'center',
        borderRadius: '5px',
        position: 'relative',
        [theme.breakpoints.up('sm')]: {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            border: `1px solid ${theme.palette.primary.main}`,
            width: '5rem',
            height: '5rem',
        },
        [theme.breakpoints.up('md')]: {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            border: `1px solid ${theme.palette.primary.main}`,
            width: '6rem',
            height: '6rem',
        },
        [theme.breakpoints.up('xl')]: {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            border: `1px solid ${theme.palette.primary.main}`,
            width: '7.5rem',
            height: '7.5rem',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            margin: '-10px',
            borderRadius: 'inherit',
            zIndex: 1,
        },
        '&::before': {
            content: '""',
            position: 'absolute',
            width: '3rem',
            height: '3rem',
            backgroundImage: `url(${numberOfEmojis === 5 ? emojisForFiveStars[label] : emojisForThreeItems[label]})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            zIndex: 0,
            borderRadius: '10px',
            [theme.breakpoints.up('sm')]: {
                width: '3.5rem',
                height: '3.5rem',
            },
            [theme.breakpoints.up('md')]: {
                width: '5rem',
                height: '5rem',
            },
            [theme.breakpoints.up('xl')]: {
                width: '6rem',
                height: '6rem',
            },
        },
    })
);

export default QuestionEmoji;
